import { Slash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export interface Crumb {
  label: string;
  to?: string;
}

/**
 * Shared navigable breadcrumb. A crumb is clickable iff it carries a `to`.
 *
 * That used to also require not being LAST, which was the same rule by a different route — every
 * caller already omits `to` on the page you are already on. Position stopped being a safe proxy
 * when the task bar dropped its own title from the trail (it is the field directly below), which
 * left the Experience crumb last and therefore dead, pointing at somewhere you were not.
 *
 * `onNavigate` lets a caller route the click through its own guard. The task editor needs it:
 * leaving with an unsaved change has to raise the modal, and that guard used to live only on a
 * back button that no longer exists.
 */
export default function NavBreadcrumb({ items, onNavigate }: {
  items: Crumb[];
  onNavigate?: (to: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <nav aria-label='Breadcrumb' className='flex min-w-0 items-center text-sm'>
      {items.map((c, i) => {
        const last = i === items.length - 1;
        return (
          <span key={i} className='flex min-w-0 items-center'>
            {i > 0 && <Slash className='mx-0.5 h-3.5 w-3.5 shrink-0 text-ws-border-strong' />}
            {c.to ? (
              <button
                onClick={() => (onNavigate ? onNavigate(c.to!) : navigate(c.to!))}
                className='max-w-[110px] shrink-0 truncate rounded-md px-1.5 py-1 text-ws-muted hover:bg-ws-hover hover:text-ws-text md:max-w-[180px]'
              >
                {c.label}
              </button>
            ) : (
              <span className={`truncate px-1.5 py-1 ${last ? 'font-medium text-ws-text' : 'text-ws-muted'}`}>{c.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
